<template>
  <b-list-group-item
    :button="!showEditShareClassForm"
    @click="showEditShareClassForm = true"
  >
    <b-row v-if="!showEditShareClassForm">
      <b-col cols="12" sm="4">
        {{ shareClass.name | decode }}
      </b-col>

      <b-col cols="6" sm="4"
        >{{ shareClass.nominalTotalShares | number }} shares
      </b-col>

      <b-col cols="6" sm="4">
        <money-format
          :value="shareClass.shareValue"
          locale="en-GB"
          :currency-code="currency"
          :subunits-value="true"
          :hide-subunits="false"
        >
        </money-format>
        each
      </b-col>
    </b-row>
    <div v-else>
      <b-row>
        <b-col cols="10">
          <h3>Update share class</h3>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-btn
            variant="outline-danger"
            @click="deleteShareClass"
            :disabled="!canDeleteShareClass"
            size="sm"
            v-b-tooltip.hover.top
            title="Delete share class"
          >
            <i class="fa fa-trash"></i>
          </b-btn>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" sm="12" xl="4">
          <base-input
            label="Name"
            v-model="shareClass.name"
            ref="name"
          ></base-input>
        </b-col>

        <b-col cols="12" sm="6" xl="4">
          <label class="form-control-label">Value</label>
          <currency-input
            class="form-control"
            v-model="shareClass.shareValue"
            :currency="currency"
            :value-as-integer="true"
          />
          <small class="text-muted">Per share</small>
        </b-col>

        <b-col>
          <base-input label="Total" v-model="shareClass.nominalTotalShares">
            <small class="text-muted" slot="infoBlock">Nominal shares</small>
          </base-input>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col cols="12" md="6">
          <b-btn @click.stop="cancel" block variant="outline-secondary">
            <i class="fa fa-times mr-2 text-muted"></i>
            Cancel
          </b-btn>
        </b-col>
        <b-col cols="12" md="6">
          <b-btn @click="update" variant="success" block>
            <span v-if="!updating">
              <i class="fa fa-check mr-2"></i>
              Save
            </span>
            <span v-else>
              <i class="fa fa-spin fa-spinner mr-2"></i>
              Please wait&hellip;
            </span>
          </b-btn>
        </b-col>
      </b-row>
    </div>
  </b-list-group-item>
</template>

<script>
import { get } from "dot-prop";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import MoneyFormat from "vue-money-format";
import _ from "underscore";

export default {
  name: "share-class-list-group-item",
  components: {
    "money-format": MoneyFormat
  },
  props: {
    accountId: {
      type: String,
      description: "Account ID",
      required: true
    },
    companyId: {
      type: String,
      description: "Company ID",
      required: true
    },
    currency: {
      type: String,
      description: "ISO Currency Code",
      required: true
    },
    shareClass: {
      type: Object,
      description: "Share Class",
      required: true
    },
    shareMovements: {
      type: Array,
      description: "Array of share movements",
      required: true
    }
  },
  data() {
    return {
      showEditShareClassForm: false,
      updating: false,
      errorMessage: null
    };
  },
  computed: {
    canDeleteShareClass() {
      // check whether share class is used in at least one share movement
      if (
        _.findWhere(this.shareMovements, { shareClassId: this.shareClass.id })
      ) {
        return false;
      }
      return true;
    }
  },
  methods: {
    cancel(event) {
      this.showEditShareClassForm = false;
    },
    update() {
      this.updating = true;

      const requestBody = {
        name: this.shareClass.name,
        shareValue: this.shareClass.shareValue,
        nominalTotalShares: this.shareClass.nominalTotalShares
      };

      this.axios
        .patch(
          `/v1/accounts/${this.accountId}/companies/${this.companyId}/share-classes/${this.shareClass.id}`,
          requestBody
        )
        .then(response => {
          this.updating = false;

          this.$notify({
            message: "Share class updated",
            timeout: 3000,
            icon: "fa fa-check",
            type: "success"
          });

          this.$emit("share-class-updated", response.data);
          this.showEditShareClassForm = false;
        })
        .catch(error => {
          this.updating = false;
          this.errorMessage = "Failed to update share class";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
    deleteShareClass() {
      swal
        .fire({
          title: "Are you sure?",
          text: "This share class will be deleted",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "gray",
          confirmButtonText: "Yes, delete!",
          reverseButtons: true,
          showLoaderOnConfirm: true,
          preConfirm: login => {
            return this.axios
              .delete(
                `/v1/accounts/${this.accountId}/companies/${this.companyId}/share-classes/${this.shareClass.id}`
              )
              .then(response => {
                return response;
              })
              .catch(error => {
                swal.showValidationMessage(
                  `Request failed: ${error.response.data.message}`
                );
              });
          },
          allowOutsideClick: () => !swal.isLoading()
        })
        .then(result => {
          if (result.value) {
            swal.fire("Done!", "The share class was removed", "success");
            // emit event so we can reload data in parent
            this.$emit("share-class-deleted", this.shareClass);
          }
        });
    }
  }
};
</script>
